import styles from "./MapUIOverlay.module.css";
import { usePosterProject } from "../../model/poster-project-provider";
import { useRef, useEffect, useState } from "react";
import { OverlayToolbox } from "./overlay-toolbox";
import { MapElement } from "PosterEditor/utils/canvas-drawing";
import { Rect } from "PosterEditor/model/poster-types";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { ContextType as EditingContextType } from "./overlay-toolbox";
import { Point } from "PosterEditor/model/poster-types";
import { Element as ProjectElement } from "PosterEditor/model/poster-types";
import {
  Backdrop,
  MapLabel,
  StaticLabel,
} from "PosterEditor/model/poster-project";
import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { set } from "lodash";

interface MapUIOverlayProps {
  pointerLocation: { x: number; y: number } | undefined;
  isDragging: boolean;
  map: React.MutableRefObject<any>;
  elementUnderCursor: MapElement | undefined;
  onShowElementEditor: (element: MapElement | undefined) => void;
}

export const MapUIOverlay = (props: MapUIOverlayProps) => {
  const {
    project,
    styledPointAtCoordinate,
    addNewMapLabel,
    addNewStaticLabel,
    addNewStyledPoint,
    addNewBackdrop,
    gpsSource,
    isTemplateEditing,
  } = usePosterProject();
  const { editorUIState, setSelectedElement, setEditingElement } =
    useEditorUI();
  const [
    selectedPointAwaitingEditingContext,
    setSelectedPointAwaitingEditingContext,
  ] = useState<MapElement | undefined>(undefined);

  const containerRef = useRef<any>(null);

  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  const [mapRect, setMapRect] = useState<Rect>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMapRect({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [props]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const updateSize = () => {
      setSize({
        width: element.offsetWidth,
        height: element.offsetHeight,
      });
    };

    // Initial size update
    updateSize();
    // Create a ResizeObserver to observe size changes
    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });

    resizeObserver.observe(element);

    // Cleanup observer on component unmount
    return () => {
      resizeObserver.unobserve(element);
    };
  }, []);

  const canSelectStaticLabels =
    isTemplateEditing ||
    Object.values(project.editingPermissions.staticLabel).some((v) => v);

  const canSelectBackdrops =
    isTemplateEditing ||
    Object.values(project.editingPermissions.backdrop).some((v) => v);

  const canSelectMapLabels =
    isTemplateEditing ||
    Object.values(project.editingPermissions.mapLabel).some((v) => v);

  useEffect(() => {
    if (props.map.current) {
      const handleClick = () => {
        if (project.isTemplateDraft) {
          return;
        }

        setSelectedPointAwaitingEditingContext(undefined);
        setEditingElement(undefined);
        setSelectedElement(undefined);

        const elementUnderCursor = props.elementUnderCursor;

        if (elementUnderCursor) {
          //Find out if there is a styled point at the cursor
          //if so, the toolbox editor will be opened for it in the sidebar.
          //if not, we bring up an option menu under the cursor.

          if (elementUnderCursor.pointCoordinate) {
            const styledPoint = styledPointAtCoordinate(
              elementUnderCursor.pointCoordinate.coordinate
            );
            if (styledPoint) {
              let projectElement: ProjectElement = {
                type: "point",
                point: elementUnderCursor.pointCoordinate.point,
                styledPoint: styledPoint.point,
              };
              setEditingElement(projectElement);
            } else if (
              elementUnderCursor.type === "mapLabel" &&
              elementUnderCursor.element
            ) {
              const label = elementUnderCursor.element as MapLabel;
              let projectElement: ProjectElement = {
                type: "mapLabel",
                id: label.id,
                point: elementUnderCursor.pointCoordinate.point,
                mapLabel: label,
              };
              if (canSelectMapLabels) {
                setEditingElement(projectElement);
              }
            } else {
              setSelectedElement(elementUnderCursor);
              setSelectedPointAwaitingEditingContext(elementUnderCursor);
            }
          } else if (
            elementUnderCursor.type === "staticLabel" &&
            elementUnderCursor.element
          ) {
            const label = elementUnderCursor.element as StaticLabel;
            let projectElement: ProjectElement = {
              type: "staticLabel",
              id: label.id,
              point: {
                x: elementUnderCursor.rect.x,
                y: elementUnderCursor.rect.y,
              },
              staticLabel: label,
            };
            if (canSelectStaticLabels) {
              setEditingElement(projectElement);
            }
          } else if (
            elementUnderCursor.type === "backdrop" &&
            elementUnderCursor.element
          ) {
            if (
              !isTemplateEditing &&
              Object.values(project.editingPermissions.backdrop).every(
                (v) => !v
              )
            ) {
              return;
            }
            const backdrop = elementUnderCursor.element as Backdrop;
            let projectElement: ProjectElement = {
              type: "backdrop",
              id: backdrop.id,
              point: {
                x: elementUnderCursor.rect.x,
                y: elementUnderCursor.rect.y,
              },
              backdrop: backdrop,
            };
            if (canSelectBackdrops) {
              setEditingElement(projectElement);
            }
          } else {
            setSelectedElement(elementUnderCursor);
            setSelectedPointAwaitingEditingContext(elementUnderCursor);
          }
        } else {
          if (selectedPointAwaitingEditingContext) {
            setSelectedElement(undefined);
            setSelectedPointAwaitingEditingContext(undefined);
          } else if (editorUIState.editingElement) {
            setEditingElement(undefined);
          } else {
            // Nothing is currently selected, so bring up the editing context menu to let the user add
            // a label.

            let mapElement: MapElement = {
              type: "point",
              rect: {
                x: props.pointerLocation?.x ?? 0,
                y: props.pointerLocation?.y ?? 0,
                width: 0,
                height: 0,
              },
            };
            setSelectedElement(mapElement);
            setSelectedPointAwaitingEditingContext(mapElement);
          }
        }
      };

      const updateSelectedPointMenuPosition = () => {
        // if (selectedPoint) {
        //   const selectedCoordinate = selectedPoint.coordinate;
        //   const movedPoint = props.map.current.project([
        //     selectedCoordinate.lng,
        //     selectedCoordinate.lat,
        //   ]);
        //   if (
        //     movedPoint.x < 0 ||
        //     movedPoint.x > size.width ||
        //     movedPoint.y < 0 ||
        //     movedPoint.y > size.height
        //   ) {
        //     setSelectedPoint(undefined);
        //     return;
        //   }
        //   setSelectedPoint({
        //     point: movedPoint,
        //     coordinate: selectedCoordinate,
        //   });
        // }
      };

      const handleMove = () => {
        // setEditingElement(undefined);
        updateSelectedPointMenuPosition();
      };

      const handleZoom = () => {
        updateSelectedPointMenuPosition();
      };

      const handleDrag = () => {
        updateSelectedPointMenuPosition();
      };

      props.map.current.on("move", handleMove);
      props.map.current.on("zoom", handleZoom);
      props.map.current.on("click", handleClick);
      props.map.current.on("drag", handleDrag);

      return () => {
        props.map.current.off("move", handleMove);
        props.map.current.off("zoom", handleZoom);
        props.map.current.off("click", handleClick);
      };
    }
    // Dependency array - ensure only necessary dependencies are here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    size.width,
    size.height,
    props.map,
    gpsSource?.displayedCoordinates,
    props.pointerLocation,
  ]);

  const pointFromRect: Point | null = selectedPointAwaitingEditingContext
    ? {
        x: selectedPointAwaitingEditingContext?.rect.x ?? 0,
        y: selectedPointAwaitingEditingContext?.rect.y ?? 0,
      }
    : null;

  const relativePointFromRect: Point | null = {
    x: (pointFromRect?.x ?? 0) / size.width,
    y: (pointFromRect?.y ?? 0) / size.height,
  };

  const onSelectEditingContextForNewElementAtSelectedPoint = (
    contextType: EditingContextType
  ) => {
    // setEditingElement(selectedPointAwaitingEditingContext);
    // setSelectedElement(selectedPointAwaitingEditingContext);
    const pointCoordinate =
      selectedPointAwaitingEditingContext?.pointCoordinate;

    if (!pointCoordinate && !pointFromRect) {
      return;
    }

    const coord =
      pointCoordinate?.coordinate ??
      (props.map.current.unproject(pointFromRect) as Coordinate);
    const point = pointCoordinate?.point ?? pointFromRect;

    if (contextType === "mapLabel" && point) {
      const label = addNewMapLabel(coord);
      const mapElement: ProjectElement = {
        type: "mapLabel",
        id: label.id,
        point: point,
        mapLabel: { ...label },
      };
      setEditingElement(mapElement);
    } else if (contextType === "staticLabel" && relativePointFromRect) {
      const label = addNewStaticLabel(relativePointFromRect, 30 / size.width);
      const mapElement: ProjectElement = {
        type: "staticLabel",
        id: label.id,
        point: relativePointFromRect,
        staticLabel: { ...label },
      };
      setEditingElement(mapElement);
    } else if (contextType === "point" && point) {
      const styledPoint = addNewStyledPoint(coord);
      const mapElement: ProjectElement = {
        type: "point",
        point: point,
        styledPoint: styledPoint,
      };
      setEditingElement(mapElement);
    } else if (contextType === "backdrop" && relativePointFromRect) {
      const backdrop = addNewBackdrop(relativePointFromRect);
      const mapElement: ProjectElement = {
        type: "backdrop",
        id: backdrop.id,
        backdrop,
      };
      setEditingElement(mapElement);
    }
  };

  const point =
    selectedPointAwaitingEditingContext?.pointCoordinate?.point ??
    pointFromRect;

  return (
    <div ref={containerRef} className={styles.container} id="map-overlay">
      {editorUIState.selectedElement &&
        !editorUIState.editingElement &&
        point && (
          <OverlayToolBox
            position={point}
            mapRect={mapRect}
            onClose={() => {
              setSelectedPointAwaitingEditingContext(undefined);
            }}
            onSelectContextType={
              onSelectEditingContextForNewElementAtSelectedPoint
            }
          />
        )}
    </div>
  );
};

const OverlayToolBox = (props: {
  position: Point;
  mapRect: Rect;
  onClose: () => void;
  onSelectContextType: (
    contextType: "point" | "mapLabel" | "staticLabel" | "backdrop"
  ) => void;
}) => {
  return (
    <OverlayToolbox
      mapRect={props.mapRect}
      onClose={props.onClose}
      position={props.position}
      onSelectContextType={props.onSelectContextType}
    />
  );
};
